<template>
    <div
        v-if="loading || cards.length === 0"
        class="flex items-center justify-center min-h-[240px]"
    >
        <ChartStatePlaceholder :loading="loading" />
    </div>
    <div v-else class="flex flex-col gap-4">
        <ul class="flex flex-col gap-6">
            <li
                v-for="(card, idx) in displayedCards"
                :key="'usage-card-' + idx"
                :class="[
                    'flex flex-col gap-1.5',
                    isTeaser(idx) && 'pointer-events-none',
                ]"
                :style="isTeaser(idx) ? teaserMaskStyle : null"
            >
                <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-2 min-w-0 flex-1">
                        <img
                            v-if="card.image"
                            :src="card.image"
                            :alt="card.name"
                            class="w-7 h-5 object-contain flex-shrink-0 rounded-sm"
                        />
                        <span
                            v-else
                            class="w-7 h-5 bg-slate-100 rounded-sm flex-shrink-0"
                        />
                        <div
                            class="flex items-baseline gap-1 min-w-0 flex-1"
                        >
                            <span
                                class="block truncate text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 min-w-0"
                                @mouseenter="onNameHover($event, card)"
                                @mouseleave="hideNameTooltip"
                            >
                                {{ card.name }}
                            </span>
                            <span
                                v-if="insightsShowCardNumbers && card.last_4"
                                class="text-slate-500 dark:text-slate-400 font-inter text-sm font-normal leading-4 shrink-0"
                            >
                                ({{ card.last_4 }})
                            </span>
                        </div>
                    </div>
                    <span
                        class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 text-right whitespace-nowrap"
                    >
                        {{ card.percent }}%
                    </span>
                </div>
                <div class="relative h-1 rounded-full bg-[#FFE0D6] overflow-hidden">
                    <div
                        class="absolute inset-y-0 left-0 bg-[#FD421D] rounded-full transition-all"
                        :style="{ width: clamp(card.percent) + '%' }"
                    />
                </div>
                <div
                    class="text-slate-500 dark:text-slate-400 font-inter text-sm font-normal leading-5"
                >
                    {{ formatMoney(card.used) }} of {{ formatMoney(card.total) }}
                    <template v-if="card.user_name">
                        · {{ card.user_name }}
                    </template>
                </div>
            </li>
        </ul>
        <button
            v-if="showToggle"
            type="button"
            class="self-start text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 hover:underline"
            @click="store.setUsageByCardExpanded(!expanded)"
        >
            {{ expanded ? 'Show less' : 'Show all' }}
        </button>
    </div>

    <!-- Card name tooltip — shown only when the card title is truncated. -->
    <Teleport to="body">
        <div
            v-if="nameTooltip.visible"
            class="fixed z-50 px-2 py-1 rounded-lg shadow-sm bg-[#1E293B] text-white text-xs whitespace-nowrap pointer-events-none"
            :style="{
                left: nameTooltip.x + 'px',
                top: nameTooltip.y + 'px',
                transform: 'translate(-50%, -100%)',
            }"
        >
            {{ nameTooltip.text }}
        </div>
    </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import ChartStatePlaceholder from './chart-state-placeholder.vue';

const props = defineProps({
    cards: {
        type: Array,
        required: true,
    },
});

const store = useCardBenefitsStore();
const { insightsShowCardNumbers, loading, usageByCardExpanded: expanded } =
    storeToRefs(store);

const COLLAPSED_LIMIT = 3;

const showToggle = computed(() => props.cards.length > COLLAPSED_LIMIT);

const displayedCards = computed(() => {
    if (expanded.value || props.cards.length <= COLLAPSED_LIMIT) {
        return props.cards;
    }
    return props.cards.slice(0, COLLAPSED_LIMIT + 1);
});

const isTeaser = (idx) =>
    !expanded.value &&
    props.cards.length > COLLAPSED_LIMIT &&
    idx === COLLAPSED_LIMIT;

const teaserMaskStyle = {
    maskImage: 'linear-gradient(to bottom, black 20%, transparent 100%)',
    WebkitMaskImage:
        'linear-gradient(to bottom, black 20%, transparent 100%)',
};

const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';
    return '$' + Math.round(Number(value)).toLocaleString('en-US');
};

// Full card identification shown in the row hover tooltip — supports the
// Figma "Add tooltips with card title for truncated cards" dev note.
const cardTooltipText = (card) => {
    if (insightsShowCardNumbers.value && card.last_4) {
        return `${card.name} (${card.last_4})`;
    }
    return card.name;
};

// Tooltip is only rendered when the card title element actually overflows.
const nameTooltip = ref({ visible: false, x: 0, y: 0, text: '' });

const onNameHover = (event, card) => {
    const el = event.currentTarget;
    if (!el || el.scrollWidth <= el.clientWidth + 1) return;
    const rect = el.getBoundingClientRect();
    nameTooltip.value = {
        visible: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        text: cardTooltipText(card),
    };
};

const hideNameTooltip = () => {
    nameTooltip.value.visible = false;
};
</script>
